import React, { useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  LinearProgress,
} from '@mui/material';
import { motion } from 'framer-motion';
import SustainableIcon from '@mui/icons-material/People';
import CommunityIcon from '@mui/icons-material/People';
import EducationIcon from '@mui/icons-material/School';
import CloseIcon from '@mui/icons-material/Close';
import SchoolIcon from '@mui/icons-material/School';
import WorkIcon from '@mui/icons-material/Work';
import GroupIcon from '@mui/icons-material/Group';
import SendIcon from '@mui/icons-material/Send';

const initiatives = [
  {
    title: 'Environmental Impact',
    description: 'Committed to reducing our carbon footprint and promoting sustainable practices.',
    progress: 75,
    icon: <SustainableIcon sx={{ fontSize: 40 }} />,
    metrics: 'Carbon neutral by 2025',
  },
  {
    title: 'Community Development',
    description: 'Supporting local communities through technology and education initiatives.',
    progress: 85,
    icon: <CommunityIcon sx={{ fontSize: 40 }} />,
    metrics: '10,000+ beneficiaries',
  },
  {
    title: 'Education & Training',
    description: 'Providing GIS training and educational resources to World institutions.',
    progress: 90,
    icon: <EducationIcon sx={{ fontSize: 40 }} />,
    metrics: '5,000+ students trained',
  },
];

const mentorshipProgram = {
  title: 'Career Mentorship Program',
  description: 'Join our comprehensive mentorship program designed to help young professionals grow in the geospatial industry.',
  details: {
    overview: 'Our mentorship program pairs experienced professionals with emerging talent to foster growth, learning, and career development in the geospatial field.',
    benefits: [
      'One-on-one mentorship with industry experts',
      'Monthly career development workshops',
      'Networking opportunities with industry leaders',
      'Access to exclusive training resources',
      'Project-based learning experiences',
    ],
    duration: '6 months',
    requirements: [
      'Background in GIS, Geography, or related field',
      'Basic understanding of geospatial concepts',
      'Commitment to program duration',
      'Willingness to learn and grow',
    ],
  },
};

const CorporateResponsibility = () => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    background: '',
    motivation: '',
  });

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission here
    console.log('Form submitted:', formData);
    handleClose();
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <Box>
      {/* Custom Hero Section */}
      <Box
        sx={{
          position: 'relative',
          minHeight: '50vh',
          display: 'flex',
          alignItems: 'center',
          paddingTop: { xs: '160px', md: '180px' },
          paddingBottom: '60px',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url("/images/responsability.jpg")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            zIndex: 0,
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: '#000',
            opacity: 0.7,
            zIndex: 1,
          },
        }}
      >
        <Container sx={{ position: 'relative', zIndex: 2 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography
              variant="h1"
              color="white"
              sx={{
                fontSize: { xs: '2.5rem', md: '4rem' },
                fontWeight: 700,
                mb: 2,
              }}
            >
              Corporate Responsibility
            </Typography>
            <Typography
              variant="h5"
              color="white"
              sx={{
                maxWidth: '800px',
                opacity: 0.9,
              }}
            >
              Building a better future through sustainable practices and community engagement
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Mentorship Program Section */}
      <Container sx={{ py: 8 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <GroupIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                    <Typography variant="h4" component="h2">
                      {mentorshipProgram.title}
                    </Typography>
                  </Box>
                  <Typography variant="body1" paragraph>
                    {mentorshipProgram.description}
                  </Typography>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      Program Benefits:
                    </Typography>
                    {mentorshipProgram.details.benefits.map((benefit, index) => (
                      <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <SchoolIcon sx={{ color: 'primary.main', mr: 1 }} />
                        <Typography variant="body2">{benefit}</Typography>
                      </Box>
                    ))}
                  </Box>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      Requirements:
                    </Typography>
                    {mentorshipProgram.details.requirements.map((req, index) => (
                      <Chip
                        key={index}
                        label={req}
                        icon={<WorkIcon />}
                        sx={{ m: 0.5 }}
                        variant="outlined"
                      />
                    ))}
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Program Duration: {mentorshipProgram.details.duration}
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    onClick={handleClickOpen}
                    endIcon={<SendIcon />}
                  >
                    Apply Now
                  </Button>
                </CardActions>
              </Card>
            </motion.div>
          </Grid>
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <Box
                component="img"
                src="/images/mentor.jpg"
                alt="Mentorship Program"
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: 1,
                }}
              />
            </motion.div>
          </Grid>
        </Grid>
      </Container>

      {/* Application Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Apply for Mentorship Program
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              name="name"
              label="Full Name"
              type="text"
              fullWidth
              variant="outlined"
              value={formData.name}
              onChange={handleChange}
              required
            />
            <TextField
              margin="dense"
              name="email"
              label="Email Address"
              type="email"
              fullWidth
              variant="outlined"
              value={formData.email}
              onChange={handleChange}
              required
            />
            <TextField
              margin="dense"
              name="background"
              label="Educational/Professional Background"
              type="text"
              fullWidth
              variant="outlined"
              multiline
              rows={3}
              value={formData.background}
              onChange={handleChange}
              required
            />
            <TextField
              margin="dense"
              name="motivation"
              label="Why do you want to join the program?"
              type="text"
              fullWidth
              variant="outlined"
              multiline
              rows={4}
              value={formData.motivation}
              onChange={handleChange}
              required
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit" variant="contained" color="primary">
              Submit Application
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Content Section */}
      <Box sx={{ py: 12, backgroundColor: '#f8f9fa' }}>
        <Container>
          <Grid container spacing={4}>
            {initiatives.map((initiative, index) => (
              <Grid item xs={12} md={4} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: index * 0.2 }}
                >
                  <Card sx={{ p: 4, height: '100%' }}>
                    <Box sx={{ color: 'primary.main', mb: 2 }}>
                      {initiative.icon}
                    </Box>
                    <Typography variant="h4" gutterBottom>
                      {initiative.title}
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      {initiative.description}
                    </Typography>
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Progress
                      </Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={initiative.progress}
                        sx={{ 
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: 'rgba(0, 128, 129, 0.1)',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: 'primary.main',
                          },
                        }}
                      />
                    </Box>
                    <Typography 
                      variant="subtitle2" 
                      color="primary"
                      sx={{ mt: 2, fontWeight: 600 }}
                    >
                      {initiative.metrics}
                    </Typography>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default CorporateResponsibility; 